// Sound search via myinstants.com, proxied to bypass CORS.
// myinstants.com embeds sound buttons with onclick="play('/media/sounds/xxx.mp3')".

const MYINSTANTS_SEARCH = 'https://www.myinstants.com/en/search/?name=';

const PROXIES = [
    (url: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    (url: string) => `https://corsproxy.io/?url=${encodeURIComponent(url)}`,
];

const fetchWithProxy = async (targetUrl: string): Promise<string> => {
    let lastError: unknown = null;
    for (const wrap of PROXIES) {
        try {
            const res = await fetch(wrap(targetUrl), { credentials: 'omit' });
            if (!res.ok) {
                lastError = new Error(`HTTP ${res.status}`);
                continue;
            }
            const text = await res.text();
            if (text && text.length > 0) return text;
        } catch (err) {
            lastError = err;
        }
    }
    throw lastError || new Error('All proxies failed');
};

export type SoundResult = {
    label: string;
    value: string; // absolute mp3 URL
};

const BASE = 'https://www.myinstants.com';

const extractSounds = (html: string): SoundResult[] => {
    const results: SoundResult[] = [];
    // Each button looks like: <button class="small-button" onclick="play('/media/sounds/foo.mp3')">Title</button>
    // Title may appear in a sibling element. Capture both the path and a nearby label.
    const buttonRegex = /onclick="play\('([^']+)'\)"[\s\S]*?(?:<\/button>|>)/g;
    // Fallback simpler regex if structure differs
    const simpleRegex = /play\('([^']+)'\)/g;

    const seen = new Set<string>();
    const push = (path: string) => {
        if (seen.has(path)) return;
        seen.add(path);
        const value = path.startsWith('http') ? path : BASE + path;
        // derive a label from the filename
        const file = path.split('/').pop() || path;
        const label = decodeURIComponent(file.replace(/\.mp3$/i, '').replace(/[-_]+/g, ' '));
        results.push({ label, value });
    };

    let match: RegExpExecArray | null;
    let used = buttonRegex;
    while ((match = used.exec(html)) !== null) {
        if (match[1]) push(match[1]);
    }
    if (results.length === 0) {
        used = simpleRegex;
        while ((match = used.exec(html)) !== null) {
            if (match[1]) push(match[1]);
        }
    }
    return results;
};

/**
 * Search myinstants.com for sound buttons matching the query.
 * Returns absolute mp3 URLs suitable for use as alarm sources.
 */
export const searchSounds = async (query: string): Promise<SoundResult[]> => {
    const targetUrl = MYINSTANTS_SEARCH + encodeURIComponent(query);
    const html = await fetchWithProxy(targetUrl);
    const sounds = extractSounds(html);
    if (sounds.length === 0) throw new Error('No sounds found for query: ' + query);
    return sounds;
};
